/**
 * Common schema validation with Zod
 * @module schemas/common
 */

import { z } from 'zod';
import { HTTP_STATUS, PROBLEM_TYPES } from '../constants/index.js';

/** UUID schema */
export const uuidSchema = z.string().uuid('Invalid UUID format');

/** Pagination schema */
export const paginationSchema = z.object({
  page: z.number().int().positive(),
  limit: z.number().int().positive().max(100),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
});

/** Pagination query params schema */
export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

/** Validation error schema */
export const validationErrorSchema = z.object({
  field: z.string(),
  message: z.string(),
  code: z.string().optional(),
});

/** RFC 7807 Problem Details schema */
export const problemDetailsSchema = z.object({
  type: z.string().url(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string(),
  instance: z.string().optional(),
  errors: z.array(validationErrorSchema).optional(),
});

/** Sort order schema */
export const sortOrderSchema = z.enum(['asc', 'desc']);

// Type inference
export type UuidInput = z.infer<typeof uuidSchema>;
export type PaginationInput = z.infer<typeof paginationSchema>;
export type PaginationQueryInput = z.infer<typeof paginationQuerySchema>;
export type ValidationErrorInput = z.infer<typeof validationErrorSchema>;
export type ProblemDetailsInput = z.infer<typeof problemDetailsSchema>;
export type SortOrderInput = z.infer<typeof sortOrderSchema>;

/**
 * Create a ProblemDetails error object
 */
export function createProblemDetails(
  status: keyof typeof HTTP_STATUS,
  detail: string,
  errors?: z.infer<typeof validationErrorSchema>[]
): z.infer<typeof problemDetailsSchema> {
  const statusCode = HTTP_STATUS[status];
  const typeMap: Record<number, string> = {
    400: PROBLEM_TYPES.BAD_REQUEST,
    401: PROBLEM_TYPES.UNAUTHORIZED,
    403: PROBLEM_TYPES.FORBIDDEN,
    404: PROBLEM_TYPES.NOT_FOUND,
    409: PROBLEM_TYPES.CONFLICT,
    422: PROBLEM_TYPES.VALIDATION_ERROR,
    500: PROBLEM_TYPES.INTERNAL_ERROR,
  };
  
  return {
    type: typeMap[statusCode] ?? PROBLEM_TYPES.INTERNAL_ERROR,
    title: status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()),
    status: statusCode,
    detail,
    errors,
  };
}
